import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import moment from 'moment';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ExportIcon from '@mui/icons-material/CloudDownload';
import FilterIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import CasesTable from './CasesTable';
import CasesTableMobile from './CasesTableMobile';
import FilterCasesModal from './FilterCasesModal';
import CaseModal from '../modals/case';
import RestrictComponent from '../shared/RestrictComponent';
import TrainingModeModal from '../modals/TrainingModeModal';
import CaseExportModal from '../modals/case/CaseExportModal';
import ExportModal from '../modals/ExportModal';
import CaseDateModal from '../modals/case/CaseDateModal';
import CaseRequestDateModal from '../modals/case/CaseRequestDateModal';
import CaseRequestDateHospitalModal from '../modals/case/CaseRequestDateHospitalModal';
import CaseApproveDateModal from '../modals/case/CaseApproveDateModal';
import CaseRequestConsultationDateModal from '../modals/case/CaseRequestConsultationDateModal';
import CaseApproveConsultationDateModal from '../modals/case/CaseApproveConsultationDateModal';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  setActiveCase,
  discardActiveCase,
  setActiveTab,
  updateCaseDoc,
  sendChangeDateHospitalEmail,
  requestCaseDateUpdate,
  approveRequestDateUpdate,
  rejectRequestDateUpdate,
  requestConsultationDateUpdate,
  approveRequestConsultationDateUpdate,
  rejectRequestConsultationDateUpdate,
  fetchCases,
} from '../../actions/casesActions';
import { toggleSidebar } from '../../actions/sidebarActions';
import { fetchHospitals } from '../../actions/adminActions';
import { fetchSurgeons } from '../../actions/surgeonsActions';

import { userRoles, caseConstants } from '../../constants';
import { procedureFlows, cmfExtendedProcedureFlows } from '../../constants/cases';

import './cases.scss';

const CASES_BACKGROUND_REFRESH_INTERVAL = 60000;

const {
  GLOBAL_ADMIN,
  COUNTRY_ADMIN,
  DISTRIBUTOR_ADMIN,
  SURGEON,
  ENGINEER,
  SALES_REP,
  LOGISTICS,
  PRACTICE_MANAGER,
  MANUFACTURER,
  RADIOLOGY,
} = userRoles;

const getNumberOfSteps = (flow) => {
  if (caseConstants.cmfExtendedProcedureFlows.includes(flow)) {
    return caseConstants.numberOfCmfExtendedSteps;
  }

  switch (flow) {
    case caseConstants.procedureFlows?.ACL:
      return caseConstants.numberOfAClSteps;
    case caseConstants.procedureFlows?.CMF:
      return caseConstants.numberOfCmfSteps;
    case caseConstants.procedureFlows?.CUSTOM:
      return caseConstants.numberOfCustomSteps;
    default:
      return caseConstants.numberOfSteps;
  }
};

const getFormattedCaseId = (caseId = '') => {
  const parts = caseId.split('_');
  return parts.length > 1 ? parts[0] : caseId;
};

const getComparableDateValue = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = moment(value).valueOf();
  return Number.isNaN(timestamp) ? value : timestamp;
};

const getComparableValue = (field, value) => {
  if (['date', 'consultationDate', 'requestedSurgeryDateNew', 'requestedConsultationDateNew'].includes(field)) {
    return getComparableDateValue(value);
  }

  if (['postponed', 'archived'].includes(field)) {
    return !!value;
  }

  return value ?? null;
};

const getOptimisticCasePatch = (caseItem) => {
  if (!caseItem?.id) {
    return null;
  }

  return {
    id: caseItem.id,
    step: caseItem.step ?? 0,
    status: caseItem.status || '',
    date: caseItem.date || null,
    consultationDate: caseItem.consultationDate || null,
    postponed: !!caseItem.postponed,
    archived: !!caseItem.archived,
    patientLastName: caseItem.patientLastName || caseItem.patientInfo?.lastName || '',
    procedureFlow: caseItem.procedureFlow || '',
    formattedId: caseItem.formattedId || getFormattedCaseId(caseItem.id),
    requestedSurgeryDateNew: caseItem?.requestedSurgeryDate?.newDate || caseItem?.requestedSurgeryDateNew || null,
    requestedConsultationDate: caseItem?.requestedConsultationDate || null,
    requestedConsultationDateNew: caseItem?.requestedConsultationDate?.newDate || caseItem?.requestedConsultationDateNew || null,
  };
};

const hasOptimisticCaseChanged = (currentCase, optimisticPatch) => {
  if (!currentCase || !optimisticPatch) {
    return false;
  }

  return [
    'step',
    'status',
    'date',
    'consultationDate',
    'postponed',
    'archived',
    'patientLastName',
    'procedureFlow',
    'formattedId',
    'requestedSurgeryDateNew',
    'requestedConsultationDateNew',
  ].some((field) => getComparableValue(field, currentCase?.[field]) !== getComparableValue(field, optimisticPatch?.[field]));
};

const matchesStatusFilter = (caseItem, statuses = []) => {
  const isArchived = !!caseItem?.archived;
  const isPostponed = !!caseItem?.postponed;
  const status = caseItem?.status || '';

  if (!statuses?.length) {
    return !isArchived && !isPostponed;
  }

  if (statuses.includes('Deleted') && isArchived) {
    return true;
  }

  if (statuses.includes('Postponed') && isPostponed) {
    return true;
  }

  if (isArchived || isPostponed) {
    return false;
  }

  if (statuses.includes('Open') && ['IN_PROGRESS', 'REJECTED'].includes(status)) {
    return true;
  }

  if (statuses.includes('Completed') && status === 'COMPLETED') {
    return true;
  }

  return false;
};

const matchesStepFilter = (caseItem, steps = []) => {
  if (!steps?.length) {
    return true;
  }

  return steps.map((step) => step.value).includes(caseItem?.step);
};

const matchesCurrentView = (caseItem, filter, trainingMode) => {
  if (!caseItem) {
    return false;
  }

  if (typeof trainingMode === 'boolean' && !!caseItem.trainingMode !== trainingMode) {
    return false;
  }

  return matchesStatusFilter(caseItem, filter?.statuses) && matchesStepFilter(caseItem, filter?.steps);
};

const CasesTableContainer = (props) => {
  const {
    title,
    setActiveCase,
    discardActiveCase,
    setActiveTab,
    toggleSidebar,
    withButton,
    onlyRequired,
    trainingMode,
    users,
    surgeons,
    caseId,
    countryList,
    distributors,
    hospitals,
    procedures,
    teams,
    userId,
    role,
    updateCaseDoc,
    requestCaseDateUpdate,
    approveRequestDateUpdate,
    sendChangeDateHospitalEmail,
    rejectRequestDateUpdate,
    requestConsultationDateUpdate,
    approveRequestConsultationDateUpdate,
    rejectRequestConsultationDateUpdate,
    fetchCases,
    companies = []
  } = props;

  const theme = useTheme();

  const dispatch = useDispatch();
  const filteredFields = ['statuses', 'hospitals', 'countries', 'procedures', 'companies'];

  const getInitialFilters = () => {
    const initialFilter = {};

    filteredFields.forEach((field) => {
      if (field === 'statuses') {
        initialFilter[field] = ['Open'];
      } else {
        initialFilter[field] = [];
      }
    });

    initialFilter.date = null;
    initialFilter.dateType = 'all';
    initialFilter.surgeons = [];

    return initialFilter;
  };

  const [filter, setFilter] = useState(getInitialFilters());
  const [isOpen, toggleModal] = useState(false);
  const [filterModalIsOpen, toggleFilterModal] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [trainingConfirmModalOpen, setTrainingConfirmModalState] = useState(false);

  const [isExportModalOpen, toggleExportModal] = useState(false);
  const [isCaseExportModalOpen, toggleCaseExportModal] = useState(false);

  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, setIsLoaded] = useState(false);

  const [dateModalOpen, toggleDateModal] = useState(false);
  const [dateHospitalModalOpen, toggleDateHospitalModal] = useState(false);
  const [requestDateModalOpen, toggleRequestDateModal] = useState(false);
  const [approveDateModalOpen, toggleApproveDateModal] = useState(false);
  const [requestConsultationDateModalOpen, toggleRequestConsultationDateModal] = useState(false);
  const [approveConsultationDateModalOpen, toggleApproveConsultationDateModal] = useState(false);

  const [cases, setCases] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalNumber, setTotalNumber] = useState(0);
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const casesRef = useRef([]);
  const requestIdRef = useRef(0);
  const activeCasesFetchesRef = useRef(0);
  const backgroundRefreshRef = useRef(null);
  const pendingOptimisticCasesRef = useRef(new Map());

  useEffect(() => {
    dispatch(fetchHospitals());
    dispatch(fetchSurgeons());
  }, [dispatch, userId]);

  useEffect(() => {
    casesRef.current = cases;
  }, [cases]);

  useEffect(() => {
    getCases().catch();
  }, [page, rowsPerPage, filter, debouncedSearch, trainingMode, order, orderBy, userId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  const buildCasesParams = () => ({
    page,
    perPage: rowsPerPage,
    sortBy: orderBy,
    sortOrder: order,
    search: debouncedSearch,
    statuses: filter?.statuses,
    hospitals: filter?.hospitals?.length ? filter.hospitals : null,
    surgeons: filter?.surgeons?.length ? filter.surgeons : null,
    countries: filter?.countries?.length ? filter.countries : null,
    procedures: filter?.procedures?.length ? filter.procedures : null,
    company: filter?.companies?.length ? filter.companies.join(',') : null,
    steps: filter?.steps?.length ? filter.steps.map((s) => s.value) : null,
    dateType: filter?.dateType || 'all',
    dateFrom: filter?.date?.from ? moment(filter.date.from).startOf('day').valueOf() - 1 : null,
    dateTo: filter?.date?.to ? moment(filter.date.to).endOf('day').valueOf() + 1 : null,
    trainingMode,
  });

  const applyOptimisticCaseUpdate = (updatedCase) => {
    const optimisticPatch = getOptimisticCasePatch(updatedCase);

    if (!optimisticPatch) {
      return false;
    }

    const currentCase = casesRef.current.find((item) => item.id === optimisticPatch.id);

    if (!currentCase) {
      return false;
    }

    const nextVisibleCase = { ...currentCase, ...optimisticPatch };
    const shouldRemove = !matchesCurrentView(nextVisibleCase, filter, trainingMode);
    const hasVisibleChange = shouldRemove || hasOptimisticCaseChanged(currentCase, optimisticPatch);

    if (!hasVisibleChange) {
      return false;
    }

    pendingOptimisticCasesRef.current.set(optimisticPatch.id, optimisticPatch);

    setCases((prev) => {
      if (shouldRemove) {
        return prev.filter((caseItem) => caseItem.id !== optimisticPatch.id);
      }

      return prev.map((caseItem) => (
        caseItem.id === optimisticPatch.id ? { ...caseItem, ...optimisticPatch } : caseItem
      ));
    });

    if (shouldRemove) {
      setTotalNumber((prev) => Math.max(0, prev - 1));
    }

    return true;
  };

  const mergeFetchedCasesWithOptimistic = (fetchedCases = []) => {
    if (!pendingOptimisticCasesRef.current.size) {
      return { cases: fetchedCases, hiddenCount: 0 };
    }

    const nextPendingOptimisticCases = new Map(pendingOptimisticCasesRef.current);
    let hiddenCount = 0;

    const mergedCases = fetchedCases.reduce((list, caseItem) => {
      const optimisticPatch = nextPendingOptimisticCases.get(caseItem.id);

      if (!optimisticPatch) {
        list.push(caseItem);
        return list;
      }

      if (!hasOptimisticCaseChanged(caseItem, optimisticPatch)) {
        nextPendingOptimisticCases.delete(caseItem.id);
        list.push(caseItem);
        return list;
      }

      const mergedCase = { ...caseItem, ...optimisticPatch };

      if (matchesCurrentView(mergedCase, filter, trainingMode)) {
        list.push(mergedCase);
      } else {
        hiddenCount += 1;
      }

      return list;
    }, []);

    pendingOptimisticCasesRef.current = nextPendingOptimisticCases;

    return { cases: mergedCases, hiddenCount };
  };

  const getCases = async ({ showLoading = true } = {}) => {
    const requestId = ++requestIdRef.current;
    activeCasesFetchesRef.current += 1;

    try {
      if (showLoading) {
        setLoading(true);
      }

      const params = buildCasesParams();
      const res = await fetchCases(params);

      if (requestId !== requestIdRef.current) {
        return res;
      }

      if (res) {
        const fetchedCases = res?.cases || [];
        const { cases: nextCases, hiddenCount } = mergeFetchedCasesWithOptimistic(fetchedCases);

        setCases(nextCases);
        setTotalNumber(Math.max(0, (res?.total || 0) - hiddenCount));
      }

      return res;
    } catch (err) {
      console.error(err);
    } finally {
      activeCasesFetchesRef.current = Math.max(0, activeCasesFetchesRef.current - 1);

      if (requestId === requestIdRef.current) {
        if (showLoading) {
          setLoading(false);
        }

        setIsLoaded(true);
      }
    }
  };

  useEffect(() => {
    backgroundRefreshRef.current = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }

      if (loading) {
        return;
      }

      if (activeCasesFetchesRef.current > 0) {
        return;
      }

      getCases({ showLoading: false }).catch();
    };
  });

  useEffect(() => {
    const refreshCases = () => backgroundRefreshRef.current?.();
    const intervalId = setInterval(refreshCases, CASES_BACKGROUND_REFRESH_INTERVAL);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        refreshCases();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      clearInterval(intervalId);

      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []);

  const onSearchChange = (value) => {
    setPage(1);
    setSearch(value);
  };

  const handleModalClose = async (isFirstStep, selectedFlow, options = {}) => {
    toggleModal(false);

    const shouldReopenCaseModal =
      isFirstStep
      && typeof isFirstStep === "boolean"
      && [procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow);

    if (isFirstStep && typeof isFirstStep === "boolean") {
      if (shouldReopenCaseModal) {
        toggleModal(true);
      } else {
        toggleCaseExportModal(true);
      }
    } else {
      applyOptimisticCaseUpdate(options?.updatedCase);
      discardActiveCase();
    }

    if (!shouldReopenCaseModal) {
      await getCases({ showLoading: false });
    }
  };

  const handleClick = (newCase) => {
    setActiveCase(newCase);
    toggleModal(true);
  };

  const goToFiles = (newCase) => {
    handleClick(newCase);
    setActiveTab(2);
  };

  const handleFilterChange = async (field, values) => {
    setFilter({ ...filter, [field]: values });
  };

  const handleRemoveFilterItem = (key, value) => {
    if (key === 'date') {
      setFilter({ ...filter, date: null });
    } else if (key === 'dateType') {
      setFilter({ ...filter, dateType: 'all' });
    } else if (key === 'surgeons') {
      setFilter({ ...filter, surgeons: filter.surgeons.filter((item) => item !== value) });
    } else {
      setFilter({ ...filter, [key]: filter[key].filter((item) => item !== value) });
    }
  };

  const onSubmitFilters = async (newFilter) => {
    setFilter({ ...filter, ...newFilter });
    toggleFilterModal(false);
  }

  const handleNewCaseClick = () => {
    if (trainingMode) {
      setTrainingConfirmModalState(true);
    } else {
      toggleModal(true);
    }
  };

  const onSkipClick = (id) => {
    toggleCaseExportModal(false);
    setActiveCase({ id });
    toggleModal(true);
  };

  const hospitalList = useMemo(() => {
    if (selectedCase && selectedCase.surgeonId) {
      const surgeon = surgeons?.find((s) => s.id === selectedCase.surgeonId);
      return surgeon ? hospitals?.filter((h) => surgeon?.hospitals?.includes(h.id)) : []
    }
    return [];
  }, [hospitals, surgeons, selectedCase]);

  // Request date functionality

  const onSubmitDate = async (date, hospitalId) => {
    try {
      setLoading(true);
      const hospital = hospitals?.find((h) => h.id === hospitalId);
      const doc = {
        date: date?.format(),
        hospital: hospital?.name || '',
        hospitalUniq: { id: hospitalId, name: hospital?.name || '' },
        postponed: false,
      };
      await updateCaseDoc(selectedCase.id, doc);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        ...doc,
        requestedSurgeryDate: null,
        requestedSurgeryDateNew: null,
      });
      sendChangeDateHospitalEmail(selectedCase.id, { ...selectedCase, ...doc }, selectedCase);
      toggleDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDate = async (date) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedSurgeryDate: {
          currentDate: selectedCase?.date || null,
          newDate: date?.format(),
        },
        requestedSurgeryDateNew: date?.format() || null,
      });
      toggleRequestDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDateHospital = async (date, hospital) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date, hospital);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedSurgeryDate: {
          currentDate: selectedCase?.date || null,
          newDate: date?.format(),
          currentHospital: selectedCase?.hospital || '',
          newHospital: hospital,
        },
        requestedSurgeryDateNew: date?.format() || null,
      });
      toggleDateHospitalModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await approveRequestDateUpdate(selectedCase);
        const nextHospital = selectedCase?.requestedSurgeryDate?.newHospital || hospitals.find((h) => h.id === selectedCase?.requestedSurgeryDateNewHospital);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          date: selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew || null,
          requestedSurgeryDate: null,
          requestedSurgeryDateNew: null,
          postponed: false,
          hospital: nextHospital?.name || selectedCase?.hospital || '',
          hospitalUniq: nextHospital
            ? {
              id: nextHospital.id || '',
              name: nextHospital.name || '',
            }
            : selectedCase?.hospitalUniq,
        });
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await rejectRequestDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          requestedSurgeryDate: null,
          requestedSurgeryDateNew: null,
        });
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveDateClick = (row) => {
    handleModalClose(undefined, undefined, { skipOptimisticSync: true });
    setSelectedCase(row);
    toggleApproveDateModal(true);
  };

  const onTBCClick = (row) => {
    handleModalClose(undefined, undefined, { skipOptimisticSync: true });
    const canDirectlyChangeCaseDate = role === userRoles.GLOBAL_ADMIN.name;
    setSelectedCase(row);
    if (canDirectlyChangeCaseDate) {
      toggleDateModal(true);
    } else {
      toggleDateHospitalModal(true);
    }
  };

  const onRequestDateClick = (row) => {
    handleModalClose(undefined, undefined, { skipOptimisticSync: true });
    setSelectedCase({ ...row, date: row.date ? moment(row.date) : null });
    toggleRequestDateModal(true);
  };

  // Request consultation date functionality

  const onSubmitRequestConsultationDate = async (date) => {
    try {
      setLoading(true);
      await requestConsultationDateUpdate(selectedCase, date);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedConsultationDate: {
          currentDate: selectedCase?.consultationDate || null,
          newDate: date?.format(),
        },
        requestedConsultationDateNew: date?.format() || null,
      });
      toggleRequestConsultationDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await approveRequestConsultationDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          consultationDate: selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew || null,
          requestedConsultationDate: null,
          requestedConsultationDateNew: null,
        });
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await rejectRequestConsultationDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          requestedConsultationDate: null,
          requestedConsultationDateNew: null,
        });
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveConsultationDateClick = (row) => {
    handleModalClose(undefined, undefined, { skipOptimisticSync: true });
    setSelectedCase(row);
    toggleApproveConsultationDateModal(true);
  };

  const onRequestConsultationDateClick = (row) => {
    handleModalClose(undefined, undefined, { skipOptimisticSync: true });
    setSelectedCase({ ...row, consultationDate: row.consultationDate ? moment(row.consultationDate) : null });
    toggleRequestConsultationDateModal(true);
  };

  const filterOptions = useMemo(() => {
    const statuses = ['Open', 'Completed', 'Deleted', 'Postponed'];
    const countries = countryList;
    const actions = [];
    const surgeons = [];
    const steps = [];

    cases.forEach((item) => {
      const { step, surgeonName } = item;
      const action = step && caseConstants.caseActions[step];

      if (action && !actions?.includes(action)) {
        actions?.push(action);
      }

      if (step && !steps?.includes(step)) {
        steps?.push(step);
      }

      if (surgeonName && !surgeons?.includes(surgeonName)) {
        surgeons?.push(surgeonName);
      }
    });

    return {
      access: statuses,
      hospitals: hospitals?.map((h) => h.name),
      procedures: procedures?.map((p) => p.name),
      action: actions,
      surgeons,
      countries: countries,
      step: steps,
    };
  }, [cases, procedures, surgeons, hospitals, countryList]);

  const list = useMemo(() => cases?.map((caseItem) => {
    let surgeonName = caseItem?.surgeonName || '';

    if (!surgeonName) {
      const surgeonUser = users.find((user) => user.uid === caseItem?.surgeon?.userId);
      surgeonName = surgeonUser
        ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
        : "";
    }
    const caseProcedure = procedures.find((procedure) => procedure.id === (caseItem.procedureId || caseItem.procedure));

    return {
      ...caseItem,
      surgeonName,
      procedureName: caseItem?.procedureName || caseProcedure?.name || caseItem?.procedure || 'No procedure',
    };
  }), [cases, users, procedures]);

  const surgeonOptions = useMemo(() => surgeons.reduce((acc, surgeon) => {
    const surgeonUser = users.find((user) => user.uid === surgeon?.userId);
    const surgeonName = surgeonUser ? `${surgeonUser.firstName} ${surgeonUser.lastName}` : '';

    if (surgeonName && !acc.includes(surgeonName)) {
      acc.push(surgeonName);
    }

    return acc;
  }, []), [surgeons, users]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Box className="cases-table-container">
        <div className="table-title-container">
          <div className="flex-1">
            <div className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }} className="m-r-sm">
                  <MuiIconButton color="primary" onClick={toggleSidebar}>
                    <MenuIcon />
                  </MuiIconButton>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  {title || 'Cases'}
                  <Box className="badge">{totalNumber}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage cases
                  </Box>
                )}
              </Box>
            </div>
          </div>

          <div className="d-flex flex-1 flex-end">
            <RestrictComponent roles={[GLOBAL_ADMIN]}>
              <Box className="m-r-lg" sx={{ display: { xs: 'none', sm: 'block' } }}>
                <IconButton
                  onClick={() => toggleExportModal(true)}
                  icon={<ExportIcon />}
                />
              </Box>
            </RestrictComponent>

            <Box className='d-flex'>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
                <Search
                  value={search}
                  placeholder="Search"
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                />
              </Box>
              {withButton && (
                <IconButton
                  onClick={() => toggleFilterModal(true)}
                  icon={<FilterIcon />}
                  sx={{ mr: 2 }}
                />
              )}
              {trainingMode ? (
                <RestrictComponent
                  roles={[
                    GLOBAL_ADMIN,
                    COUNTRY_ADMIN,
                    DISTRIBUTOR_ADMIN,
                    SURGEON,
                    ENGINEER,
                    SALES_REP,
                    LOGISTICS,
                    PRACTICE_MANAGER,
                    MANUFACTURER,
                  ]}
                >
                  <Button
                    text='Add Case'
                    onClick={() => handleNewCaseClick(true)}
                    icon={<AddIcon color='white' />}
                  />
                </RestrictComponent>
              ) : (
                <RestrictComponent excludeRoles={[MANUFACTURER, LOGISTICS, RADIOLOGY]}>
                  <Button
                    text='Add Case'
                    onClick={() => handleNewCaseClick(true)}
                    icon={<AddIcon color='white' />}
                  />
                </RestrictComponent>
              )}
            </Box>

            {/*<Box sx={{ display: { sm: 'none' } }}>*/}
            {/*  <MuiIconButton color="primary" onClick={() => toggleFilterModal(true)}>*/}
            {/*    <FilterIcon />*/}
            {/*  </MuiIconButton>*/}
            {/*</Box>*/}
          </div>
        </div>
        <Fragment>
          {/* Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <CasesTable
              cases={list}
              filter={filter}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onRemoveFilterItem={handleRemoveFilterItem}
              onClick={handleClick}
              goToFiles={goToFiles}
              distributors={distributors}
              procedures={procedures}
              hospitals={hospitals}
              onlyRequired={onlyRequired}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              totalRows={totalNumber}
              order={order}
              setOrder={setOrder}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              loading={loading}
              onAdminApproveDateClick={onAdminApproveDateClick}
              onTBCClick={onTBCClick}
              onRequestDateClick={onRequestDateClick}
              onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
              onRequestConsultationDateClick={onRequestConsultationDateClick}
              companies={companies}
            />

            {onlyRequired && (
              <LinkContainer to="/cases">
                <div className="primary pointer d-flex p-t-md p-b-md flex-center">
                  View All Cases
                </div>
              </LinkContainer>
            )}
          </Box>

          {/* Mobile */}
          <Box sx={{ display: { md: 'none' } }}>
            <CasesTableMobile
              cases={list}
              onClick={handleClick}
              withoutPagination={onlyRequired}
              companies={companies}
            />

            {onlyRequired && (
              <LinkContainer to="/cases">
                <div className="primary pointer d-flex p-t-md p-b-md flex-center">
                  View All Cases
                </div>
              </LinkContainer>
            )}
          </Box>
        </Fragment>

        <CaseModal
          open={isOpen}
          onClose={handleModalClose}
          onAdminApproveDateClick={onAdminApproveDateClick}
          onTBCClick={onTBCClick}
          onRequestDateClick={onRequestDateClick}
          onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
          onRequestConsultationDateClick={onRequestConsultationDateClick}
        />

        {/*<CaseFilterModal*/}
        {/*  open={filterModalIsOpen}*/}
        {/*  onClose={() => toggleFilterModal(false)}*/}
        {/*  filter={filter}*/}
        {/*  filterOptions={filterOptions}*/}
        {/*  handleFilterModalSubmit={handleFilterModalSubmit}*/}
        {/*  search={search}*/}
        {/*  onSearchChange={setSearch}*/}
        {/*/>*/}

        <TrainingModeModal
          open={trainingConfirmModalOpen}
          onClose={() => setTrainingConfirmModalState(false)}
          onSubmit={() => {
            setTrainingConfirmModalState(false);
            toggleModal(true);
          }}
          title="Training Mode Active"
          text="You are about to create a case for training purposes only. Please cancel and deactivate training if you wish to create an actual case."
          submitText="Proceed"
        />

        <CaseExportModal
          open={isCaseExportModalOpen}
          onClose={() => {
            toggleCaseExportModal(false);
            discardActiveCase();
          }}
          caseId={caseId}
          onSkipClick={onSkipClick}
        />

        <CaseDateModal
          open={dateModalOpen}
          onClose={() => toggleDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitDate}
          hospitals={hospitals}
          procedures={procedures}
          loading={loading}
          isAdmin={role === GLOBAL_ADMIN}
        />

        <CaseRequestDateModal
          open={requestDateModalOpen}
          onClose={() => toggleRequestDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestDate}
          loading={loading}
        />

        <CaseRequestConsultationDateModal
          open={requestConsultationDateModalOpen}
          onClose={() => toggleRequestConsultationDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestConsultationDate}
          loading={loading}
        />

        <CaseRequestDateHospitalModal
          open={dateHospitalModalOpen}
          onClose={() => toggleDateHospitalModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestDateHospital}
          loading={loading}
          hospitals={hospitals}
          surgeons={surgeons}
        />

        <CaseApproveDateModal
          open={approveDateModalOpen}
          onClose={() => toggleApproveDateModal(false)}
          selectedCase={selectedCase}
          onApprove={onApproveDate}
          onReject={onRejectDate}
          loading={loading}
          hospitals={hospitals}
        />

        <CaseApproveConsultationDateModal
          open={approveConsultationDateModalOpen}
          onClose={() => toggleApproveConsultationDateModal(false)}
          selectedCase={selectedCase}
          onApprove={onApproveConsultationDate}
          onReject={onRejectConsultationDate}
          loading={loading}
        />

        <FilterCasesModal
          filter={filter}
          open={filterModalIsOpen}
          onClose={() => toggleFilterModal(false)}
          onSubmit={onSubmitFilters}
          search={search}
          onSearchChange={onSearchChange}
          countries={countryList}
          hospitals={hospitals?.map((c) => c?.name)}
          procedures={procedures?.map((p) => p?.name)}
          surgeons={surgeonOptions}
          companies={companies}
          teams={teams}
          userId={userId}
          userRole={role}
        />

        <ExportModal
          open={isExportModalOpen}
          onClose={() => toggleExportModal(false)}
          surgeons={surgeons}
          users={users}
        />
      </Box>
    </>
  );
};

CasesTableContainer.propTypes = {
  title: PropTypes.string,
  cases: PropTypes.array,
  withButton: PropTypes.bool,
  onlyRequired: PropTypes.bool,
  setActiveCase: PropTypes.func,
  discardActiveCase: PropTypes.func,
  setActiveTab: PropTypes.func,
  toggleSidebar: PropTypes.func,
};

const mapStateToProps = (state) => {
  const procedures = state.procedures.list;
  // const cases = [...state.cases?.list, ...state.cases?.completedCases].map((item) => {
  //   const caseProcedure = procedures.find((procedure) => procedure.id === item.procedure);
  //
  //   return { ...item, procedureName: caseProcedure ? caseProcedure.name : 'No procedure' };
  // });
  const trainingMode = state.sidebar.trainingMode;

  return {
    // cases: cases.filter((item) => !!item.trainingMode === trainingMode),
    casesLoaded: state.cases.isLoaded,
    completedLoading: state?.cases?.completedLoading,
    completedLoaded: state?.cases?.completedLoaded,
    archivedLoading: state?.cases?.archivedLoading,
    archivedLoaded: state?.cases?.archivedLoaded,
    trainingMode,
    users: state.users.list,
    surgeons: state.surgeons.list,
    activeCase: state.cases.activeCase,
    caseId: state.cases?.activeCase?.id,
    countryList: state.countries.list,
    distributors: state.distributors.list,
    teams: state.teams.list,
    hospitals: state.hospitals.list,
    procedures: state.procedures.list,
    role: state.user.currentUser.role,
    userId: state.user.currentUser.uid,
    companies: state.companies.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setActiveCase: (newCase) => dispatch(setActiveCase(newCase)),
    discardActiveCase: () => dispatch(discardActiveCase()),
    setActiveTab: (tab) => dispatch(setActiveTab(tab)),
    toggleSidebar: () => dispatch(toggleSidebar()),
    updateCaseDoc: (caseId, data) => dispatch(updateCaseDoc(caseId, data)),
    sendChangeDateHospitalEmail: (caseId, data, previousData) => dispatch(sendChangeDateHospitalEmail(caseId, data, previousData)),
    requestCaseDateUpdate: (activeCase, date, hospital) => dispatch(requestCaseDateUpdate(activeCase, date, hospital)),
    approveRequestDateUpdate: (activeCase) => dispatch(approveRequestDateUpdate(activeCase)),
    rejectRequestDateUpdate: (activeCase) => dispatch(rejectRequestDateUpdate(activeCase)),
    requestConsultationDateUpdate: (activeCase, date, hospital) => dispatch(requestConsultationDateUpdate(activeCase, date, hospital)),
    approveRequestConsultationDateUpdate: (activeCase) => dispatch(approveRequestConsultationDateUpdate(activeCase)),
    rejectRequestConsultationDateUpdate: (activeCase) => dispatch(rejectRequestConsultationDateUpdate(activeCase)),
    fetchCases: (params) => dispatch(fetchCases(params)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CasesTableContainer);
